import {useMemo, useState, useEffect} from 'react'
import {Navigate, useParams} from 'react-router-dom'
import {useWorkspace} from '../../../contexts/WorkspaceContext'
import {useAppSelector, useAppDispatch} from '../../../store/hooks'
import {
    toggleCompleteSaga,
    updateTaskSaga
} from '../../../store/tasks/tasksSlice'
import {selectAllTasks, selectTasksLoading} from '../../../store/tasks/tasksSelectors'
import {
    selectAllProjects,
    selectProjectsLoading,
    selectMemberProjectIds
} from '../../../store/projects/projectsSelectors'
import {selectOpenDropdownId, selectTeamColumns} from '../../../store/ui/uiSelectors'
import {setOpenDropdown, setOpenModal, setTeamColumns} from '../../../store/ui/uiSlice'
import Section from '../../app/Section/Section'
import Member from '../../app/Member/Member'
import Link from '../../app/Link/Link'
import Empty from '../../ui/Empty/Empty'
import IconButton from '../../ui/IconButton/IconButton'
import DropdownWrapper from '@/components/ui/Dropdown/components/DropdownWrapper/DropdownWrapper'
import Dropdown from '../../ui/Dropdown/Dropdown'
import DropdownItem from '@/components/ui/Dropdown/components/DropdownItem/DropdownItem'
import OptionsIcon from '../../../assets/icons/icon-options.svg?react'
import CheckIcon from '../../../assets/icons/icon-check.svg?react'
import ArrowLeftIcon from '../../../assets/icons/icon-arrow-left.svg?react'
import css from './WorkspaceTeamPage.module.css'
import Button from "@/components/ui/Button/Button.tsx";
import PlateLoader from '../../ui/Loader/PlateLoader'

const DROPDOWN_ID = 'team-page-options'

const WorkspaceTeamPage = () => {
    const dispatch = useAppDispatch()
    const {workspace: workspaceUrlId} = useParams<{ workspace: string }>()
    const {workspace, members, loading} = useWorkspace()

    const tasks = useAppSelector(selectAllTasks)
    const projects = useAppSelector(selectAllProjects)
    const memberProjectIds = useAppSelector(selectMemberProjectIds)
    const tasksLoading = useAppSelector(selectTasksLoading)
    const projectsLoading = useAppSelector(selectProjectsLoading)
    const openDropdownId = useAppSelector(selectOpenDropdownId)
    const teamColumns = useAppSelector(selectTeamColumns)

    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const [dropdownPage, setDropdownPage] = useState<'main' | 'columns'>('main')

    useEffect(() => {
        const shouldBeOpen = openDropdownId === DROPDOWN_ID
        if (isDropdownOpen !== shouldBeOpen) {
            setIsDropdownOpen(shouldBeOpen)
            if (!shouldBeOpen) setDropdownPage('main')
        }
    }, [openDropdownId, isDropdownOpen])

    const memberProjectIdsSet = useMemo(() => new Set(memberProjectIds), [memberProjectIds])

    // Group tasks: assigneeId -> projectId -> Task[]
    const groupTasksByAssigneeAndProject = () => {
        const grouped: Record<string, Record<string, typeof tasks>> = {}
        tasks.forEach((task: any) => {
            if (task.isCompleted) return
            if (!memberProjectIdsSet.has(task.projectId)) return

            const assigneeKey = task.assigneeId || 'unassigned'

            if (!grouped[assigneeKey]) {
                grouped[assigneeKey] = {}
            }
            if (!grouped[assigneeKey][task.projectId]) {
                grouped[assigneeKey][task.projectId] = []
            }
            grouped[assigneeKey][task.projectId].push(task)
        })
        return grouped
    }

    const getProject = (projectId: string) => {
        return projects.find((p: any) => p.id === projectId)
    }

    const handleTaskToggleComplete = (taskId: string, isCompleted: boolean) => {
        dispatch(toggleCompleteSaga({taskId, isCompleted}))
    }

    const handleTaskAssign = (taskId: string, assigneeId: string | null) => {
        dispatch(updateTaskSaga({taskId, data: {assigneeId}}))
    }

    const handleEmptyTeam = () => {
        dispatch(setOpenModal({type: 'projectSettings', initialTab: 'members'}))
    }

    if (loading || tasksLoading || projectsLoading) {
        return <PlateLoader fullscreen />
    }

    if (!workspace || workspace.workspaceUrlId !== workspaceUrlId) {
        return <Navigate to={"/"}/>
    }

    const tasksByAssignee = groupTasksByAssigneeAndProject()
    const assigneeEntries = Object.entries(tasksByAssignee).filter(([key]) => key !== 'unassigned')

    const handleColumnsChange = (columns: 1 | 2 | 3) => {
        dispatch(setTeamColumns(columns))
        dispatch(setOpenDropdown(null))
    }

    return (
        <div
            className={[css.container, teamColumns === 1 && css.columns_1, teamColumns === 2 && css.columns_2, teamColumns === 3 && css.columns_3].filter(Boolean).join(' ')}>
            <div className={css.header_top_right}>
                <DropdownWrapper>
                    <IconButton grey={true} icon={<OptionsIcon />}
                                onClick={() => dispatch(setOpenDropdown(isDropdownOpen ? null : DROPDOWN_ID))}/>
                    <Dropdown isOpen={isDropdownOpen} onClose={() => dispatch(setOpenDropdown(null))}>
                        {dropdownPage === 'main' ? (
                            <DropdownItem title="Columns" onClick={() => setDropdownPage('columns')}/>
                        ) : (
                            <>
                                <DropdownItem icon={<ArrowLeftIcon/>} title="Columns"
                                              onClick={() => setDropdownPage('main')}/>
                                {([1, 2, 3] as const).map(n => (
                                    <DropdownItem
                                        key={n}
                                        title={`${n}`}
                                        rightIcon={teamColumns === n ? <CheckIcon/> : undefined}
                                        onClick={() => handleColumnsChange(n)}
                                    />
                                ))}
                            </>
                        )}
                    </Dropdown>
                </DropdownWrapper>
            </div>
            {assigneeEntries.length === 0 ? (
                <Empty title="Team" text="There's nothing here."
                       button={members.length <= 1 ? <Button title="Invite members" type="light" size="medium" onClick={handleEmptyTeam}/> : undefined}
                />
            ) : (
                assigneeEntries.map(([assigneeKey, projectMap]) => (
                    <div className={css.section} key={assigneeKey}>
                        <div className={css.sectionTitle}>
                            <Link href={`/${workspaceUrlId}/team/${assigneeKey}`}>
                                <Member userId={assigneeKey} size="medium"/>
                            </Link>
                        </div>
                        <div className={css.sectionContent}>
                            <div className={css.lists}>
                                {Object.entries(projectMap)
                                    .sort(([a], [b]) => (getProject(a)?.name || '').localeCompare(getProject(b)?.name || ''))
                                    .map(([projectId, tasks]) => {
                                        const project = getProject(projectId)
                                        return (
                                            <Section
                                                key={projectId}
                                                title={project?.name || 'Untitled'}
                                                titleHref={`/${workspaceUrlId}/projects/${projectId}`}
                                                tasks={tasks}
                                                onTaskToggleComplete={handleTaskToggleComplete}
                                                onTaskAssign={handleTaskAssign}
                                            />
                                        )
                                    })}
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>
    )
}

export default WorkspaceTeamPage
